import React, { useState, useCallback } from 'react';
import { View, Dimensions } from 'react-native';
import { Card, H5, YStack, SizableText } from 'tamagui';
import { LineChart } from 'react-native-chart-kit';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import { formatCurrency } from '@utils/formatters';

interface SalesChartProps {
  data: Array<{
    month: string;
    revenue: number;
    transactions: number;
    monthKey: string;
  }>;
  isLoading?: boolean;
}

const SalesChart: React.FC<SalesChartProps> = ({ data, isLoading }) => {
  const theme = themeSelector();
  const [chartWidth, setChartWidth] = useState(300); // Default fallback width

  // Handle container layout to get actual parent width
  const onLayout = useCallback((event) => {
    const { width } = event.nativeEvent.layout;
    // Use the actual container width minus padding for the chart
    const availableWidth = width - 32; // Account for card padding
    setChartWidth(Math.max(availableWidth, 280)); // Minimum width of 280
  }, []);

  if (isLoading || !data || data.length === 0) {
    return (
      <Card
        padding="$4"
        backgroundColor={theme ? "#ffffff" : "#1e293b"}
        borderRadius={20}
        shadowColor="#000"
        shadowOffset={{ width: 0, height: 12 }}
        shadowOpacity={theme ? 0.08 : 0.25}
        shadowRadius={24}
        elevation={12}
        borderWidth={1}
        borderColor={theme ? "rgba(0,0,0,0.05)" : "rgba(255,255,255,0.1)"}
        overflow="hidden"
      >
        <YStack space="$3" alignItems="center" minHeight={250}>
          <H5 color={theme ? "#111827" : "#f9fafb"} fontWeight="bold">
            Monthly Revenue Trend
          </H5>
          <SizableText color={theme ? "#6b7280" : "#9ca3af"}>
            {isLoading ? "Loading chart data..." : "No data available"}
          </SizableText>
        </YStack>
      </Card>
    );
  }

  // Prepare chart data
  const chartData = {
    labels: data.map(item => {
      const [year, month] = item.monthKey.split('-');
      const date = new Date(parseInt(year), parseInt(month) - 1);
      return date.toLocaleDateString('en-US', { month: 'short' });
    }).slice(-6), // Show last 6 months
    datasets: [
      {
        data: data.map(item => item.revenue).slice(-6),
        color: (opacity = 1) => `rgba(59, 130, 246, ${opacity})`, // Blue
        strokeWidth: 3,
      },
    ],
  };

  const chartConfig = {
    backgroundColor: theme ? "#ffffff" : "#1e293b",
    backgroundGradientFrom: theme ? "#ffffff" : "#1e293b",
    backgroundGradientTo: theme ? "#f9fafb" : "#374151",
    decimalPlaces: 0,
    color: (opacity = 1) => theme ? `rgba(55, 65, 81, ${opacity})` : `rgba(229, 231, 235, ${opacity})`,
    labelColor: (opacity = 1) => theme ? `rgba(107, 114, 128, ${opacity})` : `rgba(156, 163, 175, ${opacity})`,
    style: {
      borderRadius: 16,
    },
    propsForDots: {
      r: "6",
      strokeWidth: "2",
      stroke: "#3b82f6"
    },
    propsForBackgroundLines: {
      strokeDasharray: "5,5",
      stroke: theme ? "#e5e7eb" : "#4b5563",
      strokeWidth: 1,
    },
    formatYLabel: (value) => {
      const num = parseFloat(value);
      if (num >= 1000000) return `$${(num / 1000000).toFixed(1)}M`;
      if (num >= 1000) return `$${(num / 1000).toFixed(1)}K`;
      return `$${num.toFixed(0)}`;
    },
  };

  // Calculate total revenue and growth
  const totalRevenue = data.reduce((sum, item) => sum + item.revenue, 0);
  const lastMonthRevenue = data[data.length - 1]?.revenue || 0;
  const previousMonthRevenue = data[data.length - 2]?.revenue || 0;
  const monthlyGrowth = previousMonthRevenue > 0
    ? ((lastMonthRevenue - previousMonthRevenue) / previousMonthRevenue) * 100
    : 0;

  return (
    <Card
      padding="$4"
      backgroundColor={theme ? "#ffffff" : "#1e293b"}
      borderRadius={20}
      shadowColor="#000"
      shadowOffset={{ width: 0, height: 12 }}
      shadowOpacity={theme ? 0.08 : 0.25}
      shadowRadius={24}
      elevation={12}
      borderWidth={1}
      borderColor={theme ? "rgba(0,0,0,0.05)" : "rgba(255,255,255,0.1)"}
      width="100%"
      overflow="hidden"
      onLayout={onLayout}
    >
      <YStack space="$4">
        {/* Header */}
        <YStack space="$2">
          <H5 color={theme ? "#111827" : "#f9fafb"} fontWeight="bold">
            Monthly Revenue Trend
          </H5>
          <YStack space="$1">
            <SizableText size="$3" color={theme ? "#6b7280" : "#9ca3af"}>
              Total: {formatCurrency(totalRevenue)}
            </SizableText>
            {monthlyGrowth !== 0 && (
              <SizableText
                size="$2"
                color={monthlyGrowth >= 0 ? "#10b981" : "#ef4444"}
              >
                {monthlyGrowth >= 0 ? '+' : ''}{monthlyGrowth.toFixed(1)}% from previous month
              </SizableText>
            )}
          </YStack>
        </YStack>

        {/* Chart */}
        <View style={{
          alignItems: 'center',
          overflow: 'hidden',
          width: '100%',
        }}>
          <LineChart
            data={chartData}
            width={chartWidth}
            height={220}
            chartConfig={chartConfig}
            bezier
            style={{
              marginVertical: 8,
              borderRadius: 16,
            }}
            withInnerLines={true}
            withOuterLines={false}
            withVerticalLines={false}
            withHorizontalLines={true}
            fromZero={false}
            segments={4}
          />
        </View>

        {/* Legend */}
        <YStack space="$2">
          <SizableText size="$2" color={theme ? "#6b7280" : "#9ca3af"} textAlign="center">
            Last 6 months revenue trend
          </SizableText>
        </YStack>
      </YStack>
    </Card>
  );
};

export default SalesChart;
